import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ChevronLeft, 
  MapPin, 
  GraduationCap, 
  Briefcase, 
  Clock, 
  DollarSign,
  Building2,
  Calendar,
  Users,
  Info,
  ExternalLink,
  X
} from 'lucide-react';

// 主容器
const DetailContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  padding: 0;
`;

// 头部导航
const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #f8f9fa;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    transform: scale(1.05);
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
`;

// 内容区域
const ContentArea = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// 主要内容
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// 职位卡片
const JobCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  min-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
`;

// 职位头部
const JobHeader = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const ApplyButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const ApplyButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: #667eea;
  color: white;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const JobTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
`;

const CompanyName = styled.h2`
  font-size: 1.2rem;
  color: #667eea;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// 标签区域
const TagsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Tag = styled.span`
  background: ${props => props.primary ? '#667eea' : '#f8f9fa'};
  color: ${props => props.primary ? 'white' : '#495057'};
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  border: ${props => props.primary ? 'none' : '1px solid #e9ecef'};
  display: inline-block;
  min-width: fit-content;
`;

// 紧凑信息展示
const CompactInfoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const CompactInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const CompactIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  flex-shrink: 0;
`;

const CompactLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
`;

const CompactValue = styled.span`
  color: #2c3e50;
  font-weight: 400;
  margin-left: 0.25rem;
`;

// 描述区域
const DescriptionSection = styled.div`
  margin: 2.5rem 0 0.75rem 0;
`;

// 任职要求区域（较小的上边距）
const RequirementSection = styled.div`
  margin: 1.2rem 0 0.75rem 0;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  white-space: pre-line;
`;



// 在文件开头添加模态框相关的样式组件
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
  
  h3 {
    font-size: 1.25rem;  /* 20px - 主标题 */
    font-weight: 600;
    color: #2c3e50;
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  p {
    color: #6c757d;
    margin: 0 0 24px 0;
    line-height: 1.6;

    .main-message {
      font-size: 1rem;  /* 16px - 主要说明文字 */
      margin-bottom: 16px;
      color: #4b5563;
    }

    .tips-list {
      margin: 16px 0;
      padding-left: 8px;
    }

    .tip-item {
      font-size: 0.875rem;  /* 14px - 提示内容 */
      margin: 8px 0;
      position: relative;
      padding-left: 16px;
      color: #64748b;
      
      &:before {
        content: "•";
        position: absolute;
        left: 0;
        color: #667eea;
      }
    }

    .confirm-text {
      font-size: 0.875rem;  /* 14px - 确认文字 */
      margin-top: 16px;
      color: #4b5563;
    }
  }
  
  .button-group {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
    
    button {
      padding: 10px 20px;
      border-radius: 8px;
      border: none;
      font-size: 0.875rem;  /* 14px - 按钮文字 */
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &.cancel {
        background: #f8f9fa;
        color: #495057;
        border: 1px solid #e9ecef;
        
        &:hover {
          background: #e9ecef;
        }
      }
      
      &.confirm {
        background: #667eea;
        color: white;
        
        &:hover {
          background: #5a67d8;
        }
      }
    }
  }
`;

// 关闭按钮样式
const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// 内容区域包装器，用于占据剩余空间
const JobContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// 添加免责声明相关的样式组件
const DisclaimerSection = styled.div`
  margin-top: auto;
  padding: 0.75rem;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px solid #edf2f7;
`;

const DisclaimerTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  margin: 0 0 0.375rem 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const DisclaimerList = styled.ul`
  margin: 0;
  padding: 0 0 0 1rem;
  list-style-type: disc;
`;

const DisclaimerItem = styled.li`
  font-size: 0.75rem;
  line-height: 1.5;
  color: #94a3b8;
  margin-bottom: 0.25rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobData, setJobData] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);

  // 招聘类型简写转换函数
  const getRecruitmentTypeShort = (type) => {
    if (!type) return type;
    if (type.includes('校园招聘')) return '校招';
    if (type.includes('社会招聘')) return '社招';
    return type;
  };

  useEffect(() => {
    // 默认免责声明配置
    const defaultDisclaimer = {
      job_details_footer: {
        title: "免责声明",
        items: [
          "本平台仅提供职位信息展示服务，不对职位信息的真实性、准确性负责",
          "求职者在申请职位前，请仔细核实企业信息和职位要求",
          "如发现内容涉嫌侵权，请及时联系我们，我们将积极配合处理"
        ]
      },
      apply_button_warning: {
        title: "前往申请职位",
        cancel_text: "取消",
        confirm_text: "确认"
      },
      user_experience: {
        show_disclaimer_in_job_details: true,
        confirm_redirect: true,
        new_window: true
      }
    };

    // 从路由状态中获取职位数据和disclaimer
    if (location.state && location.state.jobData) {
      setJobData(location.state.jobData);
      // 如果有传递disclaimer则使用，否则使用默认配置
      setDisclaimer(location.state.disclaimer || defaultDisclaimer);
    } else {
      // 如果没有通过state传递数据，可以从本地存储或API获取
      // 兼容旧键名迁移
      const legacyResults = localStorage.getItem('jobAssistantJobResults');
      if (legacyResults && !localStorage.getItem('searchResults')) {
        localStorage.setItem('searchResults', legacyResults);
        localStorage.removeItem('jobAssistantJobResults');
      }
      const savedJobs = localStorage.getItem('searchResults');
      if (savedJobs) {
        try {
          const jobs = JSON.parse(savedJobs);
          const job = jobs.find((_, index) => index.toString() === jobId);
          if (job) {
            setJobData(job);
            // 从localStorage获取数据时，使用默认disclaimer配置
            setDisclaimer(defaultDisclaimer);
          }
        } catch (error) {
          console.error('解析保存的职位数据失败:', error);
        }
      } else {
        // 即使没有找到职位数据，也设置默认disclaimer
        setDisclaimer(defaultDisclaimer);
      }
    }
  }, [jobId, location.state]);

  const handleBack = () => {
    navigate(-1);
  };



  const handleApply = () => {
    // 获取职位链接，优先使用数据源，如果没有则使用职位链接
    const jobLink = jobData?.数据源 || jobData?.职位链接 || jobData?._original?.page_link;
    
    if (!jobLink) {
      console.warn('职位链接为空，无法跳转', jobData);
      alert('该职位暂无申请链接');
      return;
    }

    // 检查是否需要显示跳转确认
    // 默认情况下总是显示跳转确认，除非明确设置为false
    const shouldShowModal = disclaimer?.user_experience?.confirm_redirect !== false;
    
    if (shouldShowModal) {
      setShowApplyModal(true);
    } else {
      // 直接跳转
      const openInNewWindow = disclaimer?.user_experience?.new_window !== false;
      if (openInNewWindow) {
        window.open(jobLink, '_blank');
      } else {
        window.location.href = jobLink;
      }
    }
  };

  const handleConfirmApply = () => {
    setShowApplyModal(false);
    // 获取职位链接，优先使用数据源，如果没有则使用职位链接
    const jobLink = jobData?.数据源 || jobData?.职位链接 || jobData?._original?.page_link;
    
    if (jobLink) {
      const openInNewWindow = disclaimer?.user_experience?.new_window !== false;
      if (openInNewWindow) {
        window.open(jobLink, '_blank');
      } else {
        window.location.href = jobLink;
      }
    } else {
      console.warn('职位链接为空，无法跳转', jobData);
      alert('该职位暂无申请链接');
    }
  };

  const handleCancelApply = () => {
    setShowApplyModal(false);
  };

  const formatSalary = (salary) => {
    if (!salary || salary === '未指定') {
      return '薪资面议';
    }
    return salary;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '未指定') {
      return '未指定';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // 格式化为YYYY-MM-DD格式
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString;
    }
  };

  if (!jobData) {
    return (
      <DetailContainer>
        <Header>
          <HeaderContent>
            <BackButton onClick={handleBack}>
              <ChevronLeft size={20} />
            </BackButton>
            <HeaderTitle>职位详情</HeaderTitle>
          </HeaderContent>
        </Header>
        <ContentArea>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>未找到职位信息</p>
          </div>
        </ContentArea>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      <Header>
        <HeaderContent>
          <BackButton onClick={handleBack}>
            <ChevronLeft size={20} />
          </BackButton>
          <HeaderTitle>职位详情</HeaderTitle>
        </HeaderContent>
      </Header>

      <ContentArea>
        <MainContent>
          <JobCard>
            <JobContentWrapper>
            <JobHeader>
              <JobTitle>{jobData.职位名称 || '未知职位'}</JobTitle>
              <CompanyName>
                <Building2 size={20} />
                {jobData.企业名称 || '未知公司'}
              </CompanyName>
              <ApplyButtonContainer>
                <ApplyButton onClick={handleApply}>
                  申请
                  <ExternalLink size={16} />
                </ApplyButton>
              </ApplyButtonContainer>
            </JobHeader>

            <TagsSection>
              {jobData.工作性质 && (
                <Tag>{jobData.工作性质}</Tag>
              )}
              {jobData.招聘类型 && (
                <Tag>{getRecruitmentTypeShort(jobData.招聘类型)}</Tag>
              )}
            </TagsSection>

            <CompactInfoSection>
              <CompactInfoItem>
                <CompactIcon>
                  <MapPin size={16} />
                </CompactIcon>
                <CompactLabel>工作地点:</CompactLabel>
                <CompactValue>
                  {(() => {
                    // 去掉城市名称中的"市"后缀
                    const formatCity = (city) => {
                      if (!city) return city;
                      return city.replace(/市$/, '');
                    };
                    return Array.isArray(jobData.职位城市) 
                      ? jobData.职位城市.map(formatCity).join('、') 
                      : formatCity(jobData.职位城市 || '未指定');
                  })()}
                </CompactValue>
              </CompactInfoItem>

              <CompactInfoItem>
                <CompactIcon>
                  <GraduationCap size={16} />
                </CompactIcon>
                <CompactLabel>学历要求:</CompactLabel>
                <CompactValue>{jobData.学历要求 || '未指定'}</CompactValue>
              </CompactInfoItem>

              <CompactInfoItem>
                <CompactIcon>
                  <DollarSign size={16} />
                </CompactIcon>
                <CompactLabel>薪资范围:</CompactLabel>
                <CompactValue>{formatSalary(jobData.薪资范围)}</CompactValue>
              </CompactInfoItem>

              <CompactInfoItem>
                <CompactIcon>
                  <Calendar size={16} />
                </CompactIcon>
                <CompactLabel>发布时间:</CompactLabel>
                <CompactValue>{formatDate(jobData.发布日期)}</CompactValue>
              </CompactInfoItem>

              {jobData.截止日期 && jobData.截止日期 !== '未指定' && (
                <CompactInfoItem>
                  <CompactIcon>
                    <Clock size={16} />
                  </CompactIcon>
                  <CompactLabel>截止时间:</CompactLabel>
                  <CompactValue>{formatDate(jobData.截止日期)}</CompactValue>
                </CompactInfoItem>
              )}

              {jobData.职位有效期 && jobData.职位有效期 !== '未指定' && (
                <CompactInfoItem>
                  <CompactIcon>
                    <Calendar size={16} />
                  </CompactIcon>
                  <CompactLabel>有效期:</CompactLabel>
                  <CompactValue>{jobData.职位有效期}</CompactValue>
                </CompactInfoItem>
              )}

              <CompactInfoItem>
                <CompactIcon>
                  <Users size={16} />
                </CompactIcon>
                <CompactLabel>招聘人数:</CompactLabel>
                <CompactValue>{jobData.招聘人数 || '未指定'}</CompactValue>
              </CompactInfoItem>

              <CompactInfoItem>
                <CompactIcon>
                  <Briefcase size={16} />
                </CompactIcon>
                <CompactLabel>工作经验:</CompactLabel>
                <CompactValue>{jobData.工作经验 || '不限'}</CompactValue>
              </CompactInfoItem>
            </CompactInfoSection>

            {jobData.职位描述 && (
              <DescriptionSection>
                <SectionTitle>职位描述</SectionTitle>
                <SectionContent>{jobData.职位描述}</SectionContent>
              </DescriptionSection>
            )}

            {jobData.职位要求 && (
              <RequirementSection>
                <SectionTitle>任职要求</SectionTitle>
                <SectionContent>{jobData.职位要求}</SectionContent>
              </RequirementSection>
            )}
            </JobContentWrapper>
            
            {/* 免责声明 - 固定在卡片底部 */}
            {disclaimer?.job_details_footer && disclaimer?.user_experience?.show_disclaimer_in_job_details && (
              <DisclaimerSection>
                <DisclaimerTitle>
                  <Info size={14} color="#64748b" />
                  {disclaimer.job_details_footer.title}
                </DisclaimerTitle>
                <DisclaimerList>
                  {disclaimer.job_details_footer.items.map((item, index) => (
                    <DisclaimerItem key={index}>{item}</DisclaimerItem>
                  ))}
                </DisclaimerList>
              </DisclaimerSection>
            )}
          </JobCard>
        </MainContent>


      </ContentArea>
      
      {/* 跳转确认模态框 */}
      {showApplyModal && (
        <ModalOverlay>
          <ModalContent>
            <CloseButton onClick={handleCancelApply}>
              <X size={20} />
            </CloseButton>
            <h3>
              <ExternalLink size={24} color="#667eea" />
              {disclaimer?.apply_button_warning?.title || '前往申请职位'}
            </h3>
            <p>
              <div className="main-message">您即将跳转到企业官方招聘页面</div>
              <div className="tips-list">
                <div className="tip-item">建议提前准备好规范的简历</div>
                <div className="tip-item">使用「简历诊断」功能可提升简历通过率</div>
                <div className="tip-item">投递前记得保护个人信息安全</div>
              </div>
              <div className="confirm-text">是否继续？</div>
            </p>
            <div className="button-group">
              <button className="cancel" onClick={handleCancelApply}>
                {disclaimer?.apply_button_warning?.cancel_text || '取消'}
              </button>
              <button className="confirm" onClick={handleConfirmApply}>
                {disclaimer?.apply_button_warning?.confirm_text || '确认'}
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </DetailContainer>
  );
};

export default JobDetail;